import { and, eq, isNull } from "@-/com.npmjs.drizzle-orm";
import type { ActorTransaction } from "@-/dev.improve.haniverse.database.actor";
import { createOrReviveActorRow } from "@-/dev.improve.haniverse.database.actor/create-or-revive";
import { deriveCollectionMorphemeId } from "@-/dev.improve.haniverse.database.schemas.actor.identity";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";

/**
 * Put a morpheme (a grapheme or a word) in a collection. Does nothing when it is already there.
 *
 * A collection holds a morpheme through one `collection_morpheme` row whose id is derived from the
 * pair, so two devices that add the same morpheme before they sync write the same row. Removing it
 * tombstones that row and adding it again brings the row back.
 */
export async function addMorphemeToCollection(
  tx: ActorTransaction,
  { collectionId, morpheme, nowIso }: { collectionId: string; morpheme: string; nowIso: string },
): Promise<void> {
  // A live row is the membership, whatever its id (a row from an old export, say).
  const existing = await tx.query.collectionMorpheme.findFirst({
    columns: { id: true },
    where: { collectionId, morpheme },
  });
  if (existing !== undefined) {
    return;
  }
  await createOrReviveActorRow(tx, actorSchema.collectionMorpheme, {
    collectionId,
    createdAt: nowIso,
    id: deriveCollectionMorphemeId({ collectionId, morpheme }),
    morpheme,
  });
}

/** Take a morpheme out of a collection. Does nothing when it is not there. */
export async function removeMorphemeFromCollection(
  tx: ActorTransaction,
  { collectionId, morpheme }: { collectionId: string; morpheme: string },
): Promise<void> {
  await tx
    .softDelete(actorSchema.collectionMorpheme)
    .where(
      and(
        eq(actorSchema.collectionMorpheme.collectionId, collectionId),
        eq(actorSchema.collectionMorpheme.morpheme, morpheme),
        isNull(actorSchema.collectionMorpheme.deletedAt),
      ),
    );
}
